import { type V2, clamp, dist, lerp, smoothstep } from '../core/math';
import type { Rng } from '../core/rng';
import type { GroundKind } from '../render/textures';
import { alongPolyline, band, fbm2, polylineDistance, scatter } from './build';
import type { InteractableDef, MonsterPack, NpcSpawn, PortalDef, PropInstance, WaterRegion, ZoneKind, ZoneLayout } from './types';

interface Road {
  pts: V2[];
  width: number;
  layer: number;
  flatten: number;
}
interface Flat {
  c: V2;
  r: number;
  height: number | null;
  layer: number | null;
  soft: number;
}
interface River {
  pts: V2[];
  width: number;
  depth: number;
}

export interface ZoneBuilderOptions {
  id: string;
  name: string;
  subtitle?: string;
  kind: ZoneKind;
  biome: string;
  levelRange: [number, number];
  music: string;
  width: number;
  depth: number;
  ground: [GroundKind, GroundKind, GroundKind, GroundKind];
  groundScale?: [number, number, number, number];
  /** Hill amplitude in metres. */
  hills?: number;
  hillScale?: number;
  seed: number;
  ambient?: ZoneLayout['ambient'];
  /** Layer index used for steep slopes. */
  slopeLayer?: number;
  /** Layer index + noise threshold for variation patches. */
  patchLayer?: number;
  patchAmount?: number;
  waterLevel?: number;
  mapTint?: number;
}

/**
 * Composes a ZoneLayout from high-level features. The same builder calls in
 * the same order produce the same zone on every peer (all randomness comes
 * from the seeded Rng passed in).
 */
export class ZoneBuilder {
  readonly o: ZoneBuilderOptions;
  readonly minX: number;
  readonly minZ: number;
  private roads: Road[] = [];
  private flats: Flat[] = [];
  private rivers: River[] = [];
  readonly props: PropInstance[] = [];
  readonly npcs: NpcSpawn[] = [];
  readonly packs: MonsterPack[] = [];
  readonly portals: PortalDef[] = [];
  readonly interactables: InteractableDef[] = [];
  readonly spawnPoints: ZoneLayout['spawnPoints'] = {};
  readonly water: WaterRegion[] = [];
  readonly blockers: ZoneLayout['blockers'] = { circles: [], polys: [], rects: [] };
  walkable?: ZoneLayout['walkable'];
  /** Custom height contributions. */
  private heightFns: ((x: number, z: number) => number)[] = [];
  private surfaceFns: ((x: number, z: number, w: [number, number, number, number]) => void)[] = [];
  /** Occupied points to keep scatter away from features. */
  private reserved: { p: V2; r: number }[] = [];

  constructor(opts: ZoneBuilderOptions, readonly rng: Rng) {
    this.o = opts;
    this.minX = -opts.width / 2;
    this.minZ = -opts.depth / 2;
    if (opts.waterLevel !== undefined) this.water.push({ shape: { kind: 'sheet' }, level: opts.waterLevel, deep: true });
  }

  get maxX(): number {
    return this.minX + this.o.width;
  }
  get maxZ(): number {
    return this.minZ + this.o.depth;
  }

  road(pts: V2[], width = 4, layer = 1, flatten = 0.8): this {
    this.roads.push({ pts, width, layer, flatten });
    for (const p of pts) this.reserved.push({ p, r: width });
    return this;
  }

  river(pts: V2[], width = 6, depth = 1.8): this {
    this.rivers.push({ pts, width, depth });
    for (const p of pts) this.reserved.push({ p, r: width });
    return this;
  }

  /** Flattens (and optionally repaints) a round area. */
  flat(c: V2, r: number, opts: { height?: number; layer?: number; soft?: number } = {}): this {
    this.flats.push({ c, r, height: opts.height ?? null, layer: opts.layer ?? null, soft: opts.soft ?? 4 });
    this.reserved.push({ p: c, r });
    return this;
  }

  addHeight(fn: (x: number, z: number) => number): this {
    this.heightFns.push(fn);
    return this;
  }

  addSurface(fn: (x: number, z: number, w: [number, number, number, number]) => void): this {
    this.surfaceFns.push(fn);
    return this;
  }

  reserve(p: V2, r: number): this {
    this.reserved.push({ p, r });
    return this;
  }

  isReserved(p: V2, extra = 0): boolean {
    return this.reserved.some((q) => dist(p, q.p) < q.r + extra) || this.roads.some((r) => polylineDistance(p, r.pts) < r.width / 2 + 1.5 + extra);
  }

  // ---------------------------------------------------------------- terrain

  private baseHeight(x: number, z: number): number {
    const hills = this.o.hills ?? 3;
    const s = this.o.hillScale ?? 0.018;
    let h = (fbm2(x * s, z * s, this.o.seed, 4) - 0.5) * 2 * hills;
    for (const f of this.heightFns) h += f(x, z);
    return h;
  }

  heightAt = (x: number, z: number): number => {
    let h = this.baseHeight(x, z);
    for (const f of this.flats) {
      const d = dist({ x, z }, f.c);
      if (d > f.r + f.soft) continue;
      const k = 1 - smoothstep(f.r, f.r + f.soft, d);
      const target = f.height ?? this.baseHeight(f.c.x, f.c.z);
      h = lerp(h, target, k);
    }
    for (const r of this.roads) {
      const d = polylineDistance({ x, z }, r.pts);
      if (d > r.width) continue;
      const k = band(d, r.width, r.width * 0.5) * r.flatten;
      // Roads smooth out the local terrain rather than forcing one height.
      const smooth = this.baseHeight(Math.round(x / 6) * 6, Math.round(z / 6) * 6);
      h = lerp(h, smooth, k);
    }
    for (const rv of this.rivers) {
      const d = polylineDistance({ x, z }, rv.pts);
      if (d > rv.width) continue;
      const k = 1 - smoothstep(rv.width * 0.3, rv.width, d);
      const bed = (this.o.waterLevel ?? 0) - rv.depth;
      h = lerp(h, Math.min(h, bed), k);
    }
    return h;
  };

  surfaceAt = (x: number, z: number): [number, number, number, number] => {
    const w: [number, number, number, number] = [1, 0, 0, 0];
    const add = (i: number, v: number) => {
      w[i] = (w[i] ?? 0) + v;
    };
    // Variation patches.
    if (this.o.patchLayer !== undefined) {
      const n = fbm2(x * 0.05 + 100, z * 0.05 - 50, this.o.seed + 7, 3);
      const k = smoothstep(0.55 - (this.o.patchAmount ?? 0.1), 0.7, n);
      add(this.o.patchLayer, k * 1.2);
      w[0] -= k * 0.8;
    }
    // Slopes.
    if (this.o.slopeLayer !== undefined) {
      const e = 0.6;
      const dx = this.heightAt(x + e, z) - this.heightAt(x - e, z);
      const dz = this.heightAt(x, z + e) - this.heightAt(x, z - e);
      const slope = Math.hypot(dx, dz) / (2 * e);
      const k = smoothstep(0.45, 0.9, slope);
      add(this.o.slopeLayer, k * 3);
    }
    for (const f of this.flats) {
      if (f.layer === null) continue;
      const d = dist({ x, z }, f.c);
      const k = 1 - smoothstep(f.r * 0.7, f.r + 1, d);
      if (k > 0) add(f.layer, k * 3);
    }
    for (const r of this.roads) {
      const d = polylineDistance({ x, z }, r.pts);
      const k = band(d, r.width, 1.6);
      if (k > 0) add(r.layer, k * 4);
    }
    for (const rv of this.rivers) {
      const d = polylineDistance({ x, z }, rv.pts);
      const k = band(d, rv.width * 1.5, 2);
      if (k > 0) add(this.o.slopeLayer ?? 2, k * 1.5);
    }
    for (const fn of this.surfaceFns) fn(x, z, w);
    for (let i = 0; i < 4; i++) w[i] = Math.max(0, w[i] as number);
    return w;
  };

  // ---------------------------------------------------------------- content

  prop(model: string, x: number, z: number, opts: Omit<PropInstance, 'model' | 'x' | 'z'> = {}): this {
    this.props.push({ model, x, z, ...opts });
    return this;
  }

  /** Scatters props with rejection sampling; respects reserved areas. */
  scatterProps(models: { model: string; weight: number; collider?: PropInstance['collider']; scale?: [number, number]; noShadow?: boolean }[], count: number, area: { minX: number; minZ: number; maxX: number; maxZ: number }, minDist: number, accept?: (p: V2) => boolean, clearOfReserved = 1): this {
    const pts = scatter(this.rng, {
      count,
      ...area,
      minDist,
      accept: (p) => !this.isReserved(p, clearOfReserved) && (accept ? accept(p) : true) && this.heightAt(p.x, p.z) > (this.o.waterLevel ?? -99) + 0.2,
    });
    for (const p of pts) {
      const m = this.rng.weighted(models.map((x) => ({ weight: x.weight, value: x })));
      const s = m.scale ? this.rng.range(m.scale[0], m.scale[1]) : 1;
      this.props.push({ model: m.model, x: p.x, z: p.z, rot: this.rng.range(0, Math.PI * 2), scale: s, collider: m.collider, noShadow: m.noShadow });
    }
    return this;
  }

  /** Dense tree wall along the map edge except where roads exit. */
  forestBorder(models: { model: string; weight: number; scale?: [number, number] }[], depth: number, spacing: number, gaps: V2[] = []): this {
    const { minX, minZ } = this;
    const maxX = this.maxX;
    const maxZ = this.maxZ;
    const inBorder = (p: V2) => p.x < minX + depth || p.x > maxX - depth || p.z < minZ + depth || p.z > maxZ - depth;
    const pts = scatter(this.rng, { count: 4000, minX, minZ, maxX, maxZ, minDist: spacing, maxTries: 40000, accept: (p) => inBorder(p) && !gaps.some((g) => dist(g, p) < 9) && !this.roads.some((r) => polylineDistance(p, r.pts) < r.width) });
    for (const p of pts) {
      const m = this.rng.weighted(models.map((x) => ({ weight: x.weight, value: x })));
      const s = m.scale ? this.rng.range(m.scale[0], m.scale[1]) : 1;
      this.props.push({ model: m.model, x: p.x, z: p.z, rot: this.rng.range(0, Math.PI * 2), scale: s, collider: { r: 0.8 * s } });
    }
    // Hard wall just inside the edge so nobody slips between trunks.
    const inset = depth * 0.55;
    const edge = (x0: number, z0: number, x1: number, z1: number) => this.blockers.rects.push({ x: (x0 + x1) / 2, z: (z0 + z1) / 2, hw: Math.abs(x1 - x0) / 2, hd: Math.abs(z1 - z0) / 2 });
    const gapOk = (p: V2) => gaps.some((g) => dist(g, p) < 7);
    for (const s of alongPolyline([{ x: minX + inset, z: minZ + inset }, { x: maxX - inset, z: minZ + inset }, { x: maxX - inset, z: maxZ - inset }, { x: minX + inset, z: maxZ - inset }, { x: minX + inset, z: minZ + inset }], 2)) {
      if (gapOk(s.p)) continue;
      edge(s.p.x - 1.2, s.p.z - 1.2, s.p.x + 1.2, s.p.z + 1.2);
    }
    return this;
  }

  pack(p: MonsterPack): this {
    this.packs.push(p);
    this.reserved.push({ p: { x: p.x, z: p.z }, r: p.radius * 0.6 });
    return this;
  }

  npc(npc: string, x: number, z: number, facing = 0): this {
    this.npcs.push({ npc, x, z, facing });
    this.reserved.push({ p: { x, z }, r: 2 });
    return this;
  }

  portal(p: PortalDef): this {
    this.portals.push(p);
    this.reserved.push({ p: { x: p.x, z: p.z }, r: p.radius + 2 });
    return this;
  }

  interact(i: InteractableDef): this {
    this.interactables.push(i);
    this.reserved.push({ p: { x: i.x, z: i.z }, r: 1.2 });
    return this;
  }

  spawn(name: string, x: number, z: number, facing = 0): this {
    this.spawnPoints[name] = { x, z, facing };
    this.reserved.push({ p: { x, z }, r: 3 });
    return this;
  }

  /** Finds `count` random walkable-looking points in an area for gatherables/seeds. */
  randomSpots(count: number, area: { minX: number; minZ: number; maxX: number; maxZ: number }, minDist: number): V2[] {
    return scatter(this.rng, { count, ...area, minDist, accept: (p) => !this.isReserved(p, 1) && this.heightAt(p.x, p.z) > (this.o.waterLevel ?? -99) + 0.3 });
  }

  build(): ZoneLayout {
    const o = this.o;
    return {
      id: o.id,
      name: o.name,
      subtitle: o.subtitle,
      kind: o.kind,
      biome: o.biome,
      levelRange: o.levelRange,
      music: o.music,
      minX: this.minX,
      minZ: this.minZ,
      width: o.width,
      depth: o.depth,
      ground: o.ground,
      groundScale: o.groundScale,
      heightAt: this.heightAt,
      surfaceAt: this.surfaceAt,
      props: this.props,
      water: this.water,
      blockers: this.blockers,
      walkable: this.walkable,
      spawnPoints: this.spawnPoints,
      npcs: this.npcs,
      packs: this.packs,
      portals: this.portals,
      interactables: this.interactables,
      ambient: o.ambient ?? 'none',
      mapTint: o.mapTint,
    };
  }
}

export const clampToZone = (b: ZoneBuilder, p: V2, margin = 4): V2 => ({ x: clamp(p.x, b.minX + margin, b.maxX - margin), z: clamp(p.z, b.minZ + margin, b.maxZ - margin) });
